import * as React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
  Chip,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import PaginationData from 'types/models/PaginationData';
import User from 'types/models/User';
import UserRoleType from 'types/enums/UserRoleType';
import { getUsersWithPagination } from 'services/userServices';
import EditUserContext from 'contexts/EditUserContext';
import AppContext from 'contexts/AppContext';
import useDebounce from 'hooks/useDebounce';
import SearchBar from 'components/SearchBar';
import CopyEmail from 'components/common/CopyEmail';

const translateUserTypeColor = (userRoleId: UserRoleType): { bg: string; text: string } => {
  switch (userRoleId) {
    case UserRoleType.CommerceEditor:
      return { bg: '#E3F2FD', text: '#1E88E5' };
    case UserRoleType.FranchiseEditor:
      return { bg: '#F3E5F5', text: '#8E24AA' };
    case UserRoleType.VenueEditor:
      return { bg: '#E8F5E9', text: '#43A047' };
    default:
      return { bg: '#EEEEEE', text: '#616161' };
  }
};

export default (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const { setSelectedUser } = React.useContext(EditUserContext);
  const [paginationUser, setPaginationUser] = React.useState<PaginationData<User>>();
  const { currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const [searchUsersText, setSearchUserText] = React.useState('');

  const loadUsers = React.useCallback(async (page?: number, searchTerm?: string) => {
    const paginationData = await getUsersWithPagination(page, searchTerm);
    if (paginationData) setPaginationUser(paginationData);
  }, []);

  React.useEffect(() => {
    if (currentPageListNumber) {
      loadUsers(currentPageListNumber);
    }
  }, [currentPageListNumber]);

  useDebounce(() => {
    if (searchUsersText.trim()) {
      loadUsers(undefined, searchUsersText);
    } else {
      loadUsers();
    }
  }, [searchUsersText]);

  const translateUserType = (userRoleId: UserRoleType): string => {
    switch (userRoleId) {
      case UserRoleType.CommerceEditor:
        return t('listUsers-page-commerce-user');
      case UserRoleType.FranchiseEditor:
        return t('listUsers-page-franchise-user');
      case UserRoleType.VenueEditor:
        return t('listUsers-page-venue-user');
      default:
        return 'Unknown ';
    }
  };

  const renderEditButton = (user: User): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        aria-label="edit"
        onClick={() => {
          setSelectedUser(user);
          history.push('users/edit');
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderContent = (paginationData: User[]) => {
    return (
      <>
        {paginationData.map((user: User) => (
          <TableRow key={user.id}>
            <TableCell component="th" scope="row">
              <CopyEmail email={user.email} />
            </TableCell>
            <TableCell>{user.name}</TableCell>
            <TableCell className="unique-value-cell-normal">{user.refundAuthorizationCode || '-'}</TableCell>
            <TableCell>
              <Chip
                label={translateUserType(user.role)}
                style={{
                  backgroundColor: translateUserTypeColor(user.role).bg,
                  color: translateUserTypeColor(user.role).text,
                }}
                size="small"
              />
            </TableCell>
            <TableCell>{renderEditButton(user)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <>
      <SearchBar
        label={t('listUsers-page-search-placeholder')}
        value={searchUsersText}
        onChange={(e) => setSearchUserText(e.target.value)}
        onClear={() => {
          setSearchUserText('');
        }}
      />

      <Button
        className="btn-50"
        variant="contained"
        color="primary"
        onClick={() => {
          history.push('users/create');
        }}
      >
        {t('listUsers-page-create')}
      </Button>
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('listUsers-page-email')}</TableCell>
              <TableCell>{t('listUsers-page-name')}</TableCell>
              <TableCell>{t('listUsers-page-authorization-code')}</TableCell>
              <TableCell>{t('listUsers-page-role')}</TableCell>
              <TableCell>{t('listUsers-page-edit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationUser?.data && paginationUser?.data.length > 0 ? (
              renderContent(paginationUser.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listUsers-page-noUsers')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationUser && (
        <TablePagination
          component="div"
          count={paginationUser.total}
          page={paginationUser.currentPage - 1}
          onPageChange={(_, page: number) => {
            setCurrentPageListNumber(page + 1);
          }}
          rowsPerPage={paginationUser.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </>
  );
};
